import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  EditableColumnDef,
  EditableTable,
} from "../../../../../../../shared/components/table/inline-edit-table";
import {
  CustomSelect,
  CustomTextField,
} from "../../../../../../../shared/components/inputfields/form-elements";
import { StyledButton } from "../../../../../../../shared/components/buttons/custom-button";
import { ITcProductRawMaterialInformation } from "../../../../tc-models/tc-req-dto/tc-certified-product-details.req_dto";
import { useFormActions } from "../../../../../../../shared/hooks/form-builder.hooks";
import { dtrackItService } from "../../../../tc-service/dtrackit.service";
import useNotification from "../../../../../../../shared/hooks/useNotification";
import { useTransactionCertificateStore } from "../../../../../../../store/transaction-certificates.store";
import { Utills } from "../../../../../../../shared/app-configs/utilit";
import { useApi } from "../../../../../../../shared/hooks/useApi";
import { TCAppConfig } from "../../../../../../../shared/app-configs/tc-app-configs";
import { useParams } from "react-router-dom";
import { makeGetRmStandards } from "./component-payload";

interface Option {
  value: string;
  label: string;
}

interface RawMaterialsInfoProps {
  formRef: React.MutableRefObject<any>;
  rawMaterialInfoData: ITcProductRawMaterialInformation[];
  setRawMaterialInfoData: React.Dispatch<
    React.SetStateAction<ITcProductRawMaterialInformation[]>
  >;
  productData?: any; // Step 1 product info (net / supplementary / additives weights) for auto RM weight
  onValidationChange?: (isValid: boolean) => void; // Callback to notify parent about validation status
  // WITH-component mode: the RM table is auto-derived (deduped across components). Code/percentage
  // and add/delete are locked; only the user-specific term + claims category remain editable inline.
  readOnlyDerived?: boolean;
}

// Extract the base standard from a claims category value, e.g. "RCS (RCS 100)" -> "RCS",
// "GRS (No Label)" -> "GRS". Used to enforce "only 1 claims category per standard".
const extractProductStandard = (code: string): string => {
  if (!code) return "";
  const normalized = code.trim();
  if (normalized.toLowerCase() === "na") return "na";
  if (normalized.startsWith("RAS")) return "RAS";
  if (normalized.startsWith("RWS")) return "RWS";
  if (normalized.startsWith("GRS")) return "GRS";
  if (normalized.startsWith("RCS")) return "RCS";
  if (normalized.startsWith("OCS")) return "OCS";
  const parts = normalized.split(/[\s-]/);
  return parts[0]?.trim().toUpperCase() || normalized.toUpperCase();
};

const RawMaterialInfo: React.FC<RawMaterialsInfoProps> = ({
  formRef,
  rawMaterialInfoData,
  setRawMaterialInfoData,
  productData,
  onValidationChange,
  readOnlyDerived = false,
}) => {
  const [data, setData] = useState<ITcProductRawMaterialInformation[]>(
    rawMaterialInfoData || []
  );
  const { notify } = useNotification();
  const { updateField } = useFormActions("certified-products");
  const { setRestrictPopUpSave, setRawMtrlCodes, isRestrictPopUpSave } =
    useTransactionCertificateStore();
  const { TE_ID } = useParams();
  const { executeGet } = useApi();
  const [editingRowIndex, setEditingRowIndex] = useState<number>(-1);
  const [rawMaterials, setRawMaterials] = useState<Option[]>([]);
  const [groupedRawMaterials, setGroupedRawMaterials] = useState<
    { label: string; options: Option[] }[]
  >([]);
  const [rawMaterialsLoading, setRawMaterialsLoading] = useState<boolean>(false);
  // Certified-RM mapping (material_code -> standard) + TC standards + claims-category options
  const [certifiedRawMaterialCodes, setCertifiedRawMaterialCodes] = useState<any[]>([]);
  const [tcStandardList, setTcStandardList] = useState<string[]>([]);
  const [claimsCategoryOptions, setClaimsCategoryOptions] = useState<Option[]>([]);

  // ----- Auto-derivation helpers -----
  // Certified = the RM code belongs to a standard selected on this TC (per get-certified-raw-mat-codes).
  const isCertifiedRM = (code: string): boolean => {
    if (!code || !Array.isArray(certifiedRawMaterialCodes) || certifiedRawMaterialCodes.length === 0)
      return false;
    const mapping = certifiedRawMaterialCodes.find((item: any) => item.material_code === code);
    const rawStandards: string = mapping?.standard || "";
    if (!rawStandards || rawStandards.toUpperCase() === "NA") return false;
    const standards = rawStandards
      .split("/")
      .map((s: string) => s.trim())
      .filter((s: string) => s && s.toUpperCase() !== "NA");
    if (!standards.length || !Array.isArray(tcStandardList) || tcStandardList.length === 0)
      return false;
    const tcStandardsLower = tcStandardList.map((s) => String(s).toLowerCase());
    return standards.some((std) => tcStandardsLower.includes(std.toLowerCase()));
  };

  // Canonical certified resolution: the live RM∩TC-standard recompute is the source of truth whenever
  // the mapping + standards are loaded; the stored flag is only a pre-load fallback (so a reload/race
  // that didn't carry the flag can never wrongly stick on "No").
  const resolveCertified = (code: string, stored: any): boolean => {
    const ready =
      Array.isArray(certifiedRawMaterialCodes) &&
      certifiedRawMaterialCodes.length > 0 &&
      Array.isArray(tcStandardList) &&
      tcStandardList.length > 0;
    return ready ? isCertifiedRM(code) : stored === true;
  };

  const getRmStandards = makeGetRmStandards(certifiedRawMaterialCodes);

  // Claims categories offered for a row are narrowed to the RM's own certified standards
  // (e.g. RM0005 = RCS/GRS -> only RCS/GRS label grades). Falls back to the TC-standard
  // filtered list when the RM↔standard mapping isn't loaded or yields no match, so the
  // dropdown never dead-ends.
  const claimsOptionsForCode = (code: string): Option[] => {
    const standards = getRmStandards(code).map((s) => s.toLowerCase());
    if (!standards.length) return claimsCategoryOptions;
    const filtered = claimsCategoryOptions.filter((opt) =>
      standards.includes(extractProductStandard(String(opt.value)).toLowerCase())
    );
    return filtered.length ? filtered : claimsCategoryOptions;
  };

  // Total product weight = net - supplementary - additives (from Step 1).
  const getProductWeight = (): number => {
    const net = Number(productData?.tcProductNetShippingWeight ?? productData?.tcNetShippingWeight ?? 0);
    const supp = Number(productData?.tcProductSupplementaryWeight ?? 0);
    const additives = Number(productData?.tcProductAdditivesWeight ?? 0);
    return net - supp - additives;
  };

  // Auto RM weight = productWeight * (RM% / 100).
  const getRmWeight = (pct: any): number => {
    const p = Number(pct);
    if (Number.isNaN(p)) return 0;
    return (getProductWeight() * p) / 100;
  };

  // Calculate total percentage from data (excluding empty rows)
  const calculateTotalPercentage = (
    dataArray: ITcProductRawMaterialInformation[]
  ): number => {
    return dataArray.reduce((sum, item) => {
      if (item.tcProductRawMaterialCode || Number(item.tcProductRawMaterialPercentage ?? 0) > 0) {
        // The number input persists percentages as strings — coerce before summing,
        // otherwise `+` concatenates ("0" + "40" + 60 → "04060") and the total checks fail.
        return sum + (Number(item.tcProductRawMaterialPercentage) || 0);
      }
      return sum;
    }, 0);
  };

  const isTotalOneHundred = (): boolean => calculateTotalPercentage(data) === 100;
  const isTotalExceedsHundred = (): boolean => calculateTotalPercentage(data) > 100;

  // ----- Claims category validation: required for certified rows + only 1 per standard -----
  const validateClaimsCategoriesFor = (
    rows: ITcProductRawMaterialInformation[]
  ): boolean => {
    const certifiedRows = rows.filter((r) =>
      resolveCertified(r.tcProductRawMaterialCode, r.certified)
    );
    for (const r of certifiedRows) {
      if (!r.claimsCategory || !String(r.claimsCategory).trim()) {
        notify(
          `Claims Category is required for certified raw material ${r.tcProductRawMaterialCode}`,
          "error"
        );
        return false;
      }
    }
    const byStandard = new Map<string, Set<string>>();
    for (const r of certifiedRows) {
      const std = extractProductStandard(String(r.claimsCategory || ""));
      if (!std || std.toLowerCase() === "na") continue;
      if (!byStandard.has(std)) byStandard.set(std, new Set());
      byStandard.get(std)!.add(String(r.claimsCategory).trim());
    }
    for (const [std, values] of Array.from(byStandard.entries())) {
      if (values.size > 1) {
        notify(
          `Only 1 claims category per standard is allowed. Conflicting claims categories for standard ${std}.`,
          "error"
        );
        return false;
      }
    }
    return true;
  };

  const validationSchema = Yup.object().shape({
    tcProductRawMaterialCode: Yup.string()
      .required("Product Raw Material Code is required")
      .test(
        "unique-raw-material-code",
        "Product Raw Material Code must be unique",
        function (value) {
          if (!value) return true;
          const isDuplicate = data.some((item, index) => {
            if (index === editingRowIndex) return false;
            return item.tcProductRawMaterialCode === value;
          });
          return !isDuplicate;
        }
      ),
    tcProductRawMaterialPercentage: Yup.number()
      .typeError("Percentage must be a number")
      .required("Percentage is required")
      .positive("Percentage must be positive")
      .max(100, "Percentage cannot exceed 100")
      .test("percentage-max-5-decimals", "Only 5 decimal places allowed", function (value) {
        if (value != null) {
          const decimals = (String(value).split(".")[1] || "").length;
          return decimals <= 5;
        }
        return true;
      })
      .test(
        "percentage-total-not-exceed-100",
        "Total raw material percentage cannot exceed 100%",
        function (value) {
          if (editingRowIndex > -1 && value != null) {
            const total = data.reduce((sum, item, index) => {
              if (index === editingRowIndex) return sum;
              if (item.tcProductRawMaterialCode || Number(item.tcProductRawMaterialPercentage ?? 0) > 0) {
                // Coerce: stored percentages can be strings (number input), and string
                // `+` concatenation would inflate the total and reject valid 100% splits.
                return sum + (Number(item.tcProductRawMaterialPercentage) || 0);
              }
              return sum;
            }, 0);
            return total + Number(value) <= 100;
          }
          return true;
        }
      ),
    tcProductRawMaterialUserSpecificTerm: Yup.string()
      .when("tcProductRawMaterialCode", {
        is: (val: string) => val === "RM0262",
        then: (schema) =>
          schema.required(
            "Product Raw Material User Specific Term is required when Raw Material is 'Others'"
          ),
        otherwise: (schema) => schema.nullable(),
      })
      .max(255, "Maximum 255 characters allowed"),
  });

  const formik = useFormik<ITcProductRawMaterialInformation>({
    initialValues: {
      tcProductRawMaterialCode: "",
      tcProductRawMaterialPercentage: 0,
      tcProductRawMaterialUserSpecificTerm: "",
      tcProductRawMaterialCodeDescription: "",
      claimsCategory: "",
      certified: false,
    },
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: () => {},
  });

  useEffect(() => {
    if (formRef) {
      formRef.current = {
        ...formik,
        editingRowIndex,
        hasUnsavedRow: editingRowIndex !== -1,
        // Exposed so the parent's handleNext can enforce claims-category rules before advancing.
        validateClaimsCategories: () => validateClaimsCategoriesFor(data),
      };
    }
  }, [formRef, formik, editingRowIndex, data, certifiedRawMaterialCodes, tcStandardList]);

  useEffect(() => {
    setData(Array.isArray(rawMaterialInfoData) ? rawMaterialInfoData : []);
  }, [rawMaterialInfoData]);

  useEffect(() => {
    if (onValidationChange) onValidationChange(!isTotalExceedsHundred());
  }, [data, onValidationChange]);

  // Fetch RM codes (grouped SC vs Other)
  useEffect(() => {
    const getRawMaterialCodes = async () => {
      setRawMaterialsLoading(true);
      try {
        const result: any = await dtrackItService.getRawMaterialCodes(String(TE_ID || ""));
        const scRmCodes = Array.isArray(result?.data?.["sc-rmcode"])
          ? result.data["sc-rmcode"].map((t: any) => ({
              value: t.code_1,
              label: `${t.description_1} (${t.code_1})`,
            }))
          : [];
        const rmCodes = Array.isArray(result?.data?.rmcodes)
          ? result.data.rmcodes.map((t: any) => ({
              value: t.code_1,
              label: `${t.description_1} (${t.code_1})`,
            }))
          : [];
        const legacyFlatList =
          !scRmCodes.length && !rmCodes.length && Array.isArray(result?.data)
            ? result.data.map((t: any) => ({
                value: t.code_1,
                label: `${t.description_1} (${t.code_1})`,
              }))
            : [];
        const combined = [...scRmCodes, ...rmCodes, ...legacyFlatList];
        setRawMaterials(combined);
        setGroupedRawMaterials(
          [
            { label: "SC Raw Material Codes", options: scRmCodes },
            { label: "Other Raw Material Codes", options: rmCodes },
          ].filter((group) => group.options.length > 0)
        );
        setRawMtrlCodes(combined);
      } catch (err) {
        Utills.cl(`Error in Fetching Raw Material Codes ${err}`);
        notify("Failed to fetch raw material codes", "error");
      } finally {
        setRawMaterialsLoading(false);
      }
    };
    if (TE_ID) getRawMaterialCodes();
  }, [TE_ID]);

  // Fetch certified-RM mapping (material_code -> standard) for the auto "Certified" column
  useEffect(() => {
    const fetchCertifiedRawMaterials = async () => {
      try {
        const response = await executeGet(TCAppConfig.GET_CERTIFIED_RAW_MATERIAL_CODES);
        const list = response?.data?.response || response?.data;
        setCertifiedRawMaterialCodes(Array.isArray(list) ? list : []);
      } catch (error) {
        Utills.cl(`Failed to fetch certified raw materials ${error}`);
      }
    };
    fetchCertifiedRawMaterials();
  }, []);

  // Fetch TC standards (drives Certified + the claims-category filter)
  useEffect(() => {
    const getBaseDetails = async () => {
      try {
        const res: any = await executeGet(TCAppConfig.GET_TC_BASE_DETAILS_V4, { tcId: TE_ID });
        const standards = res?.data?.tcStandard;
        setTcStandardList(Array.isArray(standards) ? standards : []);
      } catch (err) {
        Utills.cl(`Error fetching Base Details: ${err}`);
      }
    };
    if (TE_ID) getBaseDetails();
  }, [TE_ID]);

  // Fetch claims-category options for the TC's standards (Claims Category dropdown)
  useEffect(() => {
    const getProductClaimsCategory = async () => {
      try {
        const response = await executeGet(TCAppConfig.GET_SC_PRODUCT_CLAIMS_CATEGORY_V4, {
          tcId: TE_ID,
        });
        const apiData = response?.data || response;
        const details = apiData?.scProductClaimsCategoryDetails || [];
        const categorySet = new Set<string>();
        details.forEach((detail: any) => {
          (detail?.scProductClaimsCategory || []).forEach((category: string) => {
            if (category) categorySet.add(category);
          });
        });
        let categories = Array.from(categorySet);
        // The seller-SC-scoped claims-category lookup returns empty whenever the seller SC
        // can't be resolved or carries no claims-category documents (common for drafts and
        // non-certified-trader sellers) — an empty dropdown dead-ends the step. Fall back to
        // the non-scoped Label-Grade (LG) ref codes; the standard filter below still narrows
        // them to this TC's standards, and the per-row filter narrows further to the RM's
        // own certified standards. Scoped values are still preferred when present.
        if (categories.length === 0) {
          try {
            const lg: any = await dtrackItService.getStandradLabelGrade();
            const lgList = Array.isArray(lg?.data) ? lg.data : [];
            categories = lgList
              .map((t: any) => t?.code_1)
              .filter((c: any): c is string => !!c);
          } catch (e) {
            Utills.cl(`LG claims-category fallback failed: ${e}`);
          }
        }
        const filtered =
          tcStandardList.length === 0
            ? categories
            : categories.filter((category) =>
                tcStandardList.some((std: string) =>
                  category.toLowerCase().includes(std.toLowerCase())
                )
              );
        setClaimsCategoryOptions(filtered.map((cat: string) => ({ value: cat, label: cat })));
      } catch (err) {
        Utills.cl(`Error fetching product claims category: ${err}`);
      }
    };
    if (TE_ID && tcStandardList.length > 0) getProductClaimsCategory();
  }, [tcStandardList, TE_ID]);

  const displayedRawMaterials: Option[] = rawMaterialsLoading
    ? [{ value: "__loading__", label: "Loading..." }]
    : rawMaterials;

  const displayedGroupedRawMaterials =
    rawMaterialsLoading || groupedRawMaterials.length === 0 ? undefined : groupedRawMaterials;

  const handleCellChange = (rowIndex: number, columnId: string, value: any) => {
    formik.setFieldValue(columnId, value);
    setData((prevData) =>
      prevData.map((row, index) =>
        index === rowIndex ? { ...row, [columnId]: value === undefined ? "" : value } : row
      )
    );
  };

  const handleAddNewRow = () => {
    const newRow: ITcProductRawMaterialInformation = {
      tcProductRawMaterialCode: "",
      tcProductRawMaterialPercentage: 0,
      tcProductRawMaterialUserSpecificTerm: "",
      tcProductRawMaterialCodeDescription: "",
      claimsCategory: "",
      certified: false,
    };
    setData((prevData) => [...prevData, newRow]);
    setEditingRowIndex(data.length);
    setRestrictPopUpSave(true);
    formik.setValues(newRow);
    formik.setTouched({});
  };

  const handleCancelEdit = () => {
    if (editingRowIndex === data.length - 1) {
      const cleanData = data.slice(0, -1);
      setData(cleanData);
      setRawMaterialInfoData(cleanData);
      updateField("rawMaterialInfo", cleanData);
    }
    setEditingRowIndex(-1);
    setRestrictPopUpSave(false);
    formik.resetForm();
    (formik as any).hasUnsavedRow = false;
  };

  const handleSaveRow = async (): Promise<boolean> => {
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      Object.values(errors).forEach((err) => notify(err as string, "error"));
      return false;
    }

    // Re-derive the certified flag from the chosen code; clear claims category for non-certified rows.
    const certified = isCertifiedRM(formik.values.tcProductRawMaterialCode);
    const updatedRow: ITcProductRawMaterialInformation = {
      ...formik.values,
      certified,
      claimsCategory: certified ? formik.values.claimsCategory || "" : "",
    };

    // Certified rows must carry a claims category.
    if (certified && (!updatedRow.claimsCategory || !String(updatedRow.claimsCategory).trim())) {
      notify("Claims Category is required for certified raw materials", "error");
      return false;
    }

    const newData = data.map((row, index) =>
      index === editingRowIndex ? updatedRow : row
    );

    // Enforce "only 1 claims category per standard" across all certified rows.
    if (!validateClaimsCategoriesFor(newData)) return false;

    setData(newData);
    setRawMaterialInfoData(newData);
    updateField("rawMaterialInfo", newData);
    setEditingRowIndex(-1);
    setRestrictPopUpSave(false);
    formik.resetForm();
    (formik as any).hasUnsavedRow = false;
    notify("Row saved successfully", "success");
    return true;
  };

  const handleEditRow = (rowIndex: number) => {
    setEditingRowIndex(rowIndex);
    setRestrictPopUpSave(true);
    formik.setValues(data[rowIndex]);
  };

  const handleDeleteRow = (rowIndex: number) => {
    const updatedData = data.filter((_, index) => index !== rowIndex);
    setData(updatedData);
    setRawMaterialInfoData(updatedData);
    updateField("rawMaterialInfo", updatedData);
    notify("Row deleted successfully", "success");
  };

  // Read-only-derived (WITH-component) inline edit: write a single field directly on a row
  // (used for the user-specific term + claims category, the only editable cells in that mode).
  const updateRowFieldDerived = (rowIndex: number, columnId: string, value: any) => {
    const updatedData = data.map((row, index) =>
      index === rowIndex ? { ...row, [columnId]: value } : row
    );
    setData(updatedData);
    setRawMaterialInfoData(updatedData);
    updateField("rawMaterialInfo", updatedData);
  };

  const columns: EditableColumnDef<ITcProductRawMaterialInformation>[] = [
    {
      accessorKey: "tcProductRawMaterialCode",
      header: "Product Raw Material Code",
      Header: (
        <span>
          {" "}
          <i style={{ color: "red" }}>* </i>Product Raw Material Code
        </span>
      ),
      renderEditCell: ({ value, onChange, rowIndex }) => (
        <CustomSelect
          label=""
          name="tcProductRawMaterialCode"
          value={value}
          options={displayedRawMaterials}
          groupedOptions={displayedGroupedRawMaterials}
          isOptionDisabled={(option: any) =>
            rawMaterialsLoading || option?.value === "__loading__"
          }
          enableSearch={true}
          placeholder="Select Raw Material Code"
          onChange={(e) => {
            const selectedCode = String(e.target.value);
            const selectedMaterial = rawMaterials.find((mat) => mat.value === selectedCode);
            const description = selectedMaterial ? selectedMaterial.label.split(" (")[0] : "";
            const certified = isCertifiedRM(selectedCode);
            onChange(selectedCode);
            formik.setFieldValue("tcProductRawMaterialCodeDescription", description);
            handleCellChange(rowIndex, "tcProductRawMaterialCodeDescription", description);
            // auto-update derived certified flag and clear claims category when not certified
            formik.setFieldValue("certified", certified);
            handleCellChange(rowIndex, "certified", certified);
            if (!certified) {
              formik.setFieldValue("claimsCategory", "");
              handleCellChange(rowIndex, "claimsCategory", "");
            }
          }}
        />
      ),
    },
    {
      // AUTO (borderless): derived from the certified-RM mapping ∩ TC standards.
      accessorKey: "certified",
      header: "Certified",
      Header: <span>Certified</span>,
      enableEditing: false,
      Cell: ({ row }: any) => {
        const certified = resolveCertified(
          row?.original?.tcProductRawMaterialCode,
          row?.original?.certified
        );
        return <span>{certified ? "Yes" : "No"}</span>;
      },
    },
    {
      accessorKey: "tcProductRawMaterialUserSpecificTerm",
      header: "Product Raw Material User Specific Term",
      Header: () => {
        const isRequired = formik.values.tcProductRawMaterialCode === "RM0262";
        return (
          <span>
            {isRequired && <i style={{ color: "red" }}>*</i>} Product Raw Material User Specific
            Term
          </span>
        );
      },
      // In WITH-component (read-only-derived) mode this is one of the only editable cells, so it
      // is rendered as an always-on inline input that writes straight back to the derived row.
      Cell: readOnlyDerived
        ? ({ row }: any) => (
            <CustomTextField
              name="tcProductRawMaterialUserSpecificTerm"
              value={row?.original?.tcProductRawMaterialUserSpecificTerm || ""}
              onChange={(e) =>
                updateRowFieldDerived(
                  row.index,
                  "tcProductRawMaterialUserSpecificTerm",
                  e.target.value
                )
              }
              placeholder="Enter Raw Material User Specific Term"
              sx={{ mb: 0 }}
            />
          )
        : undefined,
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          name="tcProductRawMaterialUserSpecificTerm"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter Raw Material User Specific Term"
          sx={{ mb: 2 }}
        />
      ),
    },
    {
      accessorKey: "tcProductRawMaterialPercentage",
      header: "Product Raw Material Percentage",
      Header: (
        <span>
          {" "}
          <i style={{ color: "red" }}>* </i>Product Raw Material Percentage
        </span>
      ),
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          name="tcProductRawMaterialPercentage"
          value={value}
          inputProps={{ max: 100, step: "0.00001" }}
          type="number"
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === "" || raw === null || raw === undefined) {
              onChange(raw);
              return;
            }
            const matched = String(raw).match(/^\d*(?:\.(\d{0,5})?)?$/);
            const num = Number(raw);
            if (matched && !Number.isNaN(num) && num >= 0 && num <= 100) {
              onChange(raw);
            }
          }}
          placeholder="Enter value (max 5 decimals)"
          sx={{ mb: 2 }}
        />
      ),
    },
    {
      // AUTO (borderless): RM weight = (net - supp - additives) * RM% / 100.
      accessorKey: "tcProductRawMaterialWeight",
      header: "Product Raw Material Weight",
      Header: <span>Product Raw Material Weight</span>,
      enableEditing: false,
      Cell: ({ row }: any) => {
        const pct = row?.original?.tcProductRawMaterialPercentage;
        const w = getRmWeight(pct);
        return <span>{pct === undefined || pct === null || pct === "" ? "" : w.toFixed(2)}</span>;
      },
    },
    {
      // USER input (bordered): claims category — dropdown for certified rows, greyed N/A otherwise.
      accessorKey: "claimsCategory",
      header: "Claims Category",
      Header: <span>Claims Category</span>,
      Cell: ({ row }: any) => {
        const certified = resolveCertified(
          row?.original?.tcProductRawMaterialCode,
          row?.original?.certified
        );
        if (!certified)
          return <span style={{ color: "rgba(0,0,0,0.4)" }}>N/A</span>;
        // WITH-component (read-only-derived): editable inline dropdown for certified rows.
        if (readOnlyDerived) {
          return (
            <CustomSelect
              label=""
              name="claimsCategory"
              value={row?.original?.claimsCategory || ""}
              options={claimsOptionsForCode(row?.original?.tcProductRawMaterialCode)}
              enableSearch={true}
              placeholder="Select Claims Category"
              onChange={(e) =>
                updateRowFieldDerived(row.index, "claimsCategory", e.target.value)
              }
            />
          );
        }
        return <span>{row?.original?.claimsCategory || ""}</span>;
      },
      renderEditCell: ({ value, onChange, rowIndex }) => {
        const code = data[rowIndex]?.tcProductRawMaterialCode || formik.values.tcProductRawMaterialCode;
        const certified = resolveCertified(code, data[rowIndex]?.certified);
        if (!certified) {
          return (
            <CustomTextField
              name="claimsCategory"
              value="N/A"
              disabled
              sx={{ mb: 2 }}
            />
          );
        }
        return (
          <CustomSelect
            label=""
            name="claimsCategory"
            value={value || ""}
            options={claimsOptionsForCode(code)}
            enableSearch={true}
            placeholder="Select Claims Category"
            onChange={(e) => onChange(e.target.value)}
          />
        );
      },
    },
  ];

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        p: 1,
        border: "2px solid rgba(128, 128, 128, .2)",
        my: 4,
      }}
    >
      <Box display="flex" alignItems="center" mb={3} justifyContent="space-between" width="100%">
        <Typography variant="body2" color="text.secondary">
          {readOnlyDerived
            ? "This table is auto-derived from the components. You may only set the User Specific Term and Claims Category."
            : "RM % should add up to 100. Certified & Raw Material Weight are auto-calculated."}
        </Typography>
        {!readOnlyDerived && (
          <StyledButton
            disabled={isRestrictPopUpSave || isTotalOneHundred()}
            onClick={handleAddNewRow}
            sx={{ mb: 2 }}
            title={
              isTotalOneHundred()
                ? "Total percentage has reached 100%"
                : "Add new raw material entry"
            }
          >
            Add Raw Material
          </StyledButton>
        )}
      </Box>
      <EditableTable
        data={data}
        columns={columns}
        onSaveRow={handleSaveRow}
        onCellChange={handleCellChange}
        editingRowIndex={editingRowIndex}
        onEditRow={handleEditRow}
        onCancelEdit={handleCancelEdit}
        onDeleteRow={handleDeleteRow}
        hideEdit={readOnlyDerived}
        hideDelete={readOnlyDerived}
      />
    </Box>
  );
};

export default RawMaterialInfo;
